import React, { useState, useEffect, useContext, useRef } from 'react';
import './UserGroupManagement.css';
import HorizontalTabPanel from '../HorizontalTabPanel/HorizontalTabPanel';
import Panel from '../TabPanel/Panel';
import UserManagement from '../UserManagement/UserManagement';
import GroupManagement from '../GroupManagement/GroupManagement';
import convenienceApi from '../../common/api/convenience';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import { AdminContext } from '../../contexts/AdminContext';
import logComponent from '../../common/logComponent';

export default function UserGroupManagement({ updateCurrentSubPanel, currentSubPanel }) {
  const environmentContext = useContext(EnvironmentContext);
  const adminContext = useContext(AdminContext);
  const { hasToken, activeUser: user } = environmentContext;
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(false);
  const isLoading = useRef(false);

  if (logComponent('UserGroupManagement')) { console.debug('UserGroupManagement rendered'); }


  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (logComponent('UserGroupManagement')) { console.debug('UserGroupManagement onMount'); }
    if (!hasToken) return;
  }, [])

  useEffect(() => {
    if (logComponent('UserGroupManagement')) { console.debug('UserGroupManagement useEffect [environmentContext.hasToken, adminContext.shouldUpdateGroups]'); }
    if (!hasToken) return;
    loadGroups();
  }, [environmentContext.hasToken, adminContext.shouldUpdateGroups])

  const loadGroups = () => {
    if (logComponent('UserGroupManagement')) { console.debug('UserGroupManagement loadGroups'); }
    if (!hasToken || isLoading.current) return;
    isLoading.current = true;
    if (adminContext.isFresh('groups')) return;

    setGroupsLoading(true);
    setGroupsError(false);

    convenienceApi.getGroupsByAdmin(user)
      .then(result => {
        adminContext.setGroups(result);
        setGroupsLoading(false);
        setGroupsError(false);
      })
      .catch(error => {
        setGroupsLoading(false);
        setGroupsError(true);
      })
      isLoading.current = false;
  }

  const updateCurrentPanel = (subPanelTitle) => {
    if (logComponent('UserGroupManagement')) { console.debug('UserGroupManagement updateCurrentPanel'); }
    updateCurrentSubPanel('Users & Groups', subPanelTitle);
  }

  const addGroup = (group) => {
    if (logComponent('UserGroupManagement')) { console.debug('UserGroupManagement addGroup'); }
    const prevGroups = adminContext.groups ? adminContext.groups : [];
    adminContext.setGroups([group, ...prevGroups]);
  }

  const updateGroup = (updatedGroup) => {
    if (logComponent('UserGroupManagement')) { console.debug('UserGroupManagement updateGroup'); }
    const updatedGroups = adminContext.groups.map(group => group.groupId !== updatedGroup.groupId ? group : updatedGroup);
    adminContext.setGroups(updatedGroups);
  }

  const deleteGroup = (deletedGroup) => {
    if (logComponent('UserGroupManagement')) { console.debug('UserGroupManagement deleteGroup'); }

    const filteredGroups =
      adminContext.groups ?
        adminContext.groups.filter(group => group.groupId !== deletedGroup.groupId) :
        [];

    adminContext.setGroups(filteredGroups);
  }

  return (
    <HorizontalTabPanel
      currentPanel={currentSubPanel}
      updateCurrentPanel={updateCurrentPanel}
      tabTitle="Users & Groups">
      <Panel
        tabTitle="Users"
        panelTitle="Manage Users"
        linkPath="/admin/users"
        hideIf={!user.isAdmin}>
        <UserManagement
          groupsLoading={groupsLoading}
          groupsError={groupsError}
          reloadGroups={loadGroups} />
      </Panel>
      <Panel
        tabTitle="Groups"
        panelTitle="Manage Groups"
        linkPath="/admin/groups"
        hideIf={user.groupTypeString === 'Family' || !user.isAdmin}>
        <GroupManagement
          adminUser={user}
          usersLoading={usersLoading}
          usersError={usersError}
          groupsLoading={groupsLoading}
          groupsError={groupsError}
          addGroup={addGroup}
          deleteGroup={deleteGroup}
          updateGroup={updateGroup}
          reload={loadGroups}        />
      </Panel>
    </HorizontalTabPanel>
  );
}
